<template>
  <v-container class="productContainer">
    <div class="detailLayout">
      <div class="shopNavigationButtons detailNavigation">
        <v-btn class="backButton" @click="$router.go(-1)">
          <v-icon class="buttonIcon">arrow_back</v-icon>
          {{ $t("BUTTON_BACK_TO_PRODUCTS") }}
        </v-btn>
      </div>
      <StickyDiscountBanner v-if="hasCoupons" class="detailDiscount" full-width :suppress-floating="configuring"
                            :get-boundary="getBoundary" :auto-open-on-mobile="false"/>
      <div class="detailImage">
        <v-img :src="product.imageUrl" class="productImage" contain></v-img>
      </div>
      <div class="detailContent">
        <v-row v-show="error">
          <v-col cols="12">
            <v-alert type="error" v-show="error" transition="fade-transition" class="addedToBasketAlert">{{
                error
              }}
            </v-alert>
          </v-col>
        </v-row>
        <div class="productDetails elevation-3">
          <div class="productInfo">
            <ProductDiscountBadge :price="price" :instead-of="product.setPriceInsteadOf"/>
            <header class="productHeader">
              <h1 class="display-1">{{ product.name }}</h1>
            </header>
            <div class="detailBody">
              <div class="description" v-html="product.longDescription"></div>
              <ProductActions class="detailActions" :price="price" :instead-of="product.setPriceInsteadOf"
                              :from="product.detailedConfigurationSurcharge > 0"
                              :label="$t('BUTTON_ADD_PRODUCT')" icon="add" @configure="startConfiguration"/>
            </div>
          </div>
          <ProductConfigurationDialog ref="configurationDialog" v-model="configuring" :product="product"
                                      @addedToBasket="onAddedToBasket" @error="onConfigurationError"/>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script lang="ts">
import Component from "vue-class-component";
import {ProductDto} from "@swagger";
import StickyDiscountBanner from "@/components/StickyDiscountBanner.vue";
import ProductActions from "@/components/ProductActions.vue";
import ProductDiscountBadge from "@/components/ProductDiscountBadge.vue";
import {elementFromRef} from "@/global/ShopLayout";
import BasicComponent from "@/global/BasicComponent";
import ProductConfigurationDialog from "@/components/product-configuration/ProductConfigurationDialog.vue";

@Component({
  components: {StickyDiscountBanner, ProductActions, ProductDiscountBadge, ProductConfigurationDialog}
})
export default class ProductDetails extends BasicComponent {

  error: string|null = null;
  continueShopping: boolean = false;
  configuring: boolean = false;

  getBoundary(): HTMLElement | null {
    return elementFromRef(this.$el);
  }

  startConfiguration() {
    this.error = null;
    if (this.product.imageRequirements.length === 0) {
      this.addUnconfiguredProduct(this.product, item => {
        if (!item) return;
        this.configuring = true;
        this.$nextTick(() => (this.$refs.configurationDialog as ProductConfigurationDialog).onAddedToBasket([item]));
      }, error => this.onConfigurationError(error));
      return;
    }
    this.configuring = true;
  }

  /**
   * Called when an article has been added to the basket
   */
  onAddedToBasket(): void {
    this.error = undefined;
    this.continueShopping = true;
  }

  onConfigurationError(error: string): void {
    this.error = error;
  }


  get price() {
    return this.product.price ?? 0;
  }

  get product(): ProductDto {
    const product = this.$store.getters.product(this.$route.params.id);

    if (product) {
      this.trackPage("P: " + product.name);
    }
    return product ? product : {
      imageRequirements: [],
      detailedImageRequirements: []
    };
  }

}
</script>
<style scoped lang="scss">
@use "../stylesheets/Variables.scss" as *;


.container {
  margin-top: 0 !important;
}

.productContainer {
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
  max-width: 100%;
}

.detailNavigation {

  justify-content: flex-start;

  align-self: start;

}
.detailLayout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px 24px;
}
.detailImage, .detailContent {
  min-width: 0;
}
.detailDiscount {
  width: 100%;
}
@media (min-width: 960px) {
  .detailLayout {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .detailNavigation {
    grid-column: 1;
    grid-row: 1;
  }
  .detailDiscount {
    grid-column: 2;
    grid-row: 1;
  }
  .detailImage {
    grid-column: 1;
    grid-row: 2;
  }
  .detailContent {
    grid-column: 2;
    grid-row: 2;
  }
}

.productDetails {
  position: relative;
  background-color: $shop-white;
  padding: 15px;
  margin-bottom: 15px;
}

.productInfo {
  display: flow-root;
  > .discountBadge {
    float: right;
    margin: 0 0 8px 16px;
  }
}

.productHeader {
  margin-bottom: 16px;
  h1 {
    overflow-wrap: anywhere;
  }
}

// The body starts below the title/badge, which is also the upper sticky boundary.
.detailBody {
  display: flow-root;
  clear: both;
}

@media (max-width: 959px) {
  .detailActions {
    position: sticky;
    bottom: 0;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
    // The list's lift transform is unnecessary on this native sticky element.
    transform: none;
  }

  .productHeader {
    margin-bottom: 8px;
  }
}

.description {
  margin-bottom: 15px;
}

.productImage {
  max-width: 500px;
  max-height: 500px;
  margin: 0 auto;

  @media screen and (max-height: 800px) {
    max-height: 350px;
  }
}


.addedToBasketAlert {

  position: fixed;
  top: 5px;
  right: 5px;

  z-index: 1000;

  @media screen and (max-width: 470px) {
    margin: 0;
    top: 0;
    right: 0;
  }

  .basketLink {
    color: $shop-white;
    font-weight: bold;
    text-decoration: none;
  }
}


</style>
